import type { HelpContent } from './entries.tr';
import type { HELP_FAZ8_TR } from './entries.faz8.tr';

type Loc = Pick<HelpContent, 'title' | 'short'>;

/** المرحلة 8 (المالية والتحليلات) — عناوين المساعدة والنصوص المختصرة بالعربية (ADR-801). المصطلحات: docs/I18N_GLOSSARY.md. */
export const HELP_FAZ8_AR: Record<keyof typeof HELP_FAZ8_TR, Loc> = {
  'page.sales_report': {
    title: 'تقرير المبيعات',
    short: 'الطلبات، وصافي المبيعات، ومتوسط قيمة الطلب، والمرتجعات والإلغاءات، والعملاء الجدد والعائدون.',
  },
  'reports.period': {
    title: 'الفترة',
    short:
      'الأيام التي يشملها التقرير (بتوقيت تركيا، مع احتساب يومي البداية والنهاية). الفترة السابقة بالطول نفسه وتسبقها مباشرةً.',
  },
  'reports.interval': {
    title: 'التجميع',
    short: 'حسب اليوم أو الأسبوع (يبدأ يوم الاثنين) أو الشهر أو السنة.',
  },
  'reports.download': { title: 'تنزيل', short: 'ينزّل التقرير بتنسيق CSV أو Excel (XLSX).' },
  'sales_report.store': { title: 'المتجر', short: 'طلبات المتجر المختار فقط.' },
  'sales_report.channel': {
    title: 'القناة',
    short: 'متجر الويب، أو بوابة الموزّعين، أو طلب من لوحة التحكم، أو API.',
  },
  'sales_report.segment': { title: 'نوع العميل', short: 'طلبات التجزئة أو طلبات الموزّعين (الشركات).' },
  'sales_report.summary': {
    title: 'من الإجمالي إلى الصافي',
    short:
      'إجمالي مبيعات المنتجات مطروحًا منه الخصومات ومضافًا إليه الشحن هو قيمة الطلبات؛ وبطرح المرتجعات والإلغاءات نصل إلى صافي المبيعات.',
  },
  'sales_report.new_customers': {
    title: 'العملاء الجدد والعائدون',
    short: 'العميل الجديد: طلبه هو أول طلب مؤكد للحساب نفسه أو للبريد الإلكتروني نفسه.',
  },
  'dashboard.sales': {
    title: 'ملخص المبيعات',
    short: 'صافي المبيعات وعدد الطلبات اليوم وخلال آخر 30 يومًا، مقارنةً بالأمس وبفترة الـ30 يومًا السابقة.',
  },
  'sales_report.breakdown': {
    title: 'التوزيع',
    short:
      'صافي المبيعات حسب المنتج أو المتغير أو الفئة أو العلامة التجارية أو الدولة أو القناة أو المتجر أو نوع العميل أو طريقة الدفع.',
  },
  'page.finance_inputs': {
    title: 'المدخلات المالية',
    short: 'التكاليف المستخدمة في حساب الربح: قواعد رسوم الدفع، وتكاليف الشحن، ومصاريف التشغيل.',
  },
  'finance_inputs.fee_rule': {
    title: 'قاعدة رسوم الدفع',
    short: 'النسبة والرسم الثابت لكل معاملة حسب طريقة الدفع (ولعدد الأقساط اختياريًا)، مع تواريخ السريان.',
  },
  'finance_inputs.shipment_cost': {
    title: 'تكلفة الشحن',
    short:
      'المبلغ المدفوع لشركة الشحن مقابل الشحنة (شامل ضريبة القيمة المضافة)، مع قيمة الضريبة ورقم الفاتورة.',
  },
  'finance_inputs.import': {
    title: 'رفع فاتورة الشحن',
    short: 'يطابق ملف فاتورة شركة الشحن (CSV/Excel) مع الشحنات عبر رقم التتبع؛ وتُعرض معاينة أولًا.',
  },
  'finance_inputs.shipping_rule': {
    title: 'قاعدة تقدير تكلفة الشحن',
    short: 'تكلفة تقريبية لكل شحنة حسب شركة الشحن ووزن الطرد (دون ضريبة القيمة المضافة).',
  },
  'finance_inputs.expense': {
    title: 'مصروف تشغيلي',
    short:
      'الإيجار والرواتب والبرمجيات والتغليف وغيرها؛ المبلغ شامل ضريبة القيمة المضافة، وتُدخَل الضريبة منفصلة.',
  },
  'page.finance_report': {
    title: 'التقرير المالي',
    short: 'الربحية، والتدفق النقدي، وملخص ضريبة القيمة المضافة، والمستحقات؛ وربحية المنتجات والقنوات.',
  },
  'finance_report.status': {
    title: 'حالة البيانات',
    short: 'مكتملة، أو جزئية (بعضها تقديري أو غير معروف)، أو لا توجد بيانات (ناقصة، ولا تُحتسب صفرًا).',
  },
  'finance_report.cash': {
    title: 'التدفق النقدي',
    short:
      'الداخل: المدفوعات وتحصيلات الموزّعين؛ الخارج: المبالغ المستردة والرسوم وشركات الشحن والمصاريف والإعلانات.',
  },
  'finance_report.vat': {
    title: 'ملخص ضريبة القيمة المضافة',
    short:
      'الضريبة المحتسبة على المبيعات مقابل الضريبة المدفوعة على المصاريف والشحن. لا يُغني عن الإقرار الضريبي.',
  },
  'finance_report.products': {
    title: 'ربحية المنتجات والقنوات',
    short: 'الإيراد وتكلفة البضاعة المباعة وإجمالي الربح لكل منتج؛ وهامش المساهمة لكل قناة.',
  },
  'page.stock_report': {
    title: 'تقرير المخزون',
    short:
      'قيمة المخزون حسب المستودع والفئة، ومعدل الدوران، والمنتجات بطيئة الحركة؛ وملخص تواريخ انتهاء الصلاحية والمخزون الحرج.',
  },
  'stock_report.value': {
    title: 'قيمة المخزون',
    short:
      'الكمية المتوفرة × التكلفة الحالية (دون ضريبة القيمة المضافة)؛ ولا تُقيَّم الأصناف التي ليس لها تكلفة.',
  },
  'stock_report.turnover': {
    title: 'معدل الدوران وأيام المخزون',
    short: 'الوحدات المباعة ÷ متوسط المخزون؛ والأيام = أيام الفترة ÷ معدل الدوران.',
  },
  'stock_report.slow': {
    title: 'بطيء الحركة',
    short: 'متوفر في المخزون دون أي مبيعات خلال عدد الأيام المختار (الافتراضي 90).',
  },
  'stock_report.warehouse': {
    title: 'المستودع',
    short: 'عند اختياره تُحسب جميع الأرقام من حركات ذلك المستودع فقط.',
  },
  'page.suppliers': { title: 'المورّدون', short: 'الشركات التي تشتري منها، وأرصدتها وأداؤها.' },
  'page.supplier': { title: 'المورّد', short: 'بيانات المورّد، وكشف حسابه، وأوامر الشراء.' },
  'page.purchase_orders': {
    title: 'المشتريات',
    short: 'طلبات الشراء وأوامر الشراء المرسلة إلى المورّدين.',
  },
  'page.purchase_order': {
    title: 'أمر الشراء',
    short: 'البنود، والكميات المستلمة والمتبقية، وعمليات الاستلام.',
  },
  'page.purchase_order_new': {
    title: 'طلب شراء جديد',
    short: 'طلب شراء يتضمن المورّد والمستودع والمنتجات.',
  },
  'page.goods_receipt': {
    title: 'استلام البضائع',
    short: 'استلم توريدات المورّد إلى المخزون مقابل أوامر الشراء.',
  },
  'purchasing.code': { title: 'رمز المورّد', short: 'رمز قصير فريد (أحرف وأرقام ونقطة وشرطة).' },
  'purchasing.payment_terms': {
    title: 'شروط الدفع',
    short: 'تاريخ استحقاق الفاتورة = يوم الاستلام + هذا العدد من الأيام. 0: نقدًا.',
  },
  'purchasing.lead_time': {
    title: 'مدة التوريد',
    short: 'الأيام المتوقعة من الطلب حتى التسليم؛ تُستخدم للتاريخ المتوقع ونسبة التسليم في الموعد.',
  },
  'purchasing.unit_cost': {
    title: 'تكلفة الوحدة وضريبة القيمة المضافة',
    short:
      'تكلفة الوحدة دون ضريبة القيمة المضافة بعملة الطلب؛ وإذا تُركت الضريبة فارغة تُؤخذ من فئة الضريبة للمنتج.',
  },
  'purchasing.place_order': {
    title: 'إرسال الطلب',
    short: 'يحوّل طلب الشراء إلى أمر شراء مُرسَل؛ ولا تُرسَل أي رسالة إلى المورّد.',
  },
  'purchasing.receive': {
    title: 'استلام البضائع',
    short: 'لا يمكن أن تتجاوز الكمية الكمية المتبقية ما لم يُقبَل التسليم الزائد.',
  },
  'purchasing.update_cost': {
    title: 'تحديث التكلفة',
    short: 'متوسط التكلفة المرجّح المتحرك للمخزون الحالي والبضائع المستلمة.',
  },
  'purchasing.ledger_entry': {
    title: 'قيد في حساب المورّد',
    short: 'الدفعات والمرتجعات تخفّض الدين؛ وتُصحَّح القيود بقيد عكسي.',
  },
  'purchasing.performance': {
    title: 'أداء المورّد',
    short: 'التسليم في الموعد، ونسبة التلبية، والبنود الناقصة/الزائدة، ومدة التوريد.',
  },
  'page.report_builder': {
    title: 'منشئ التقارير',
    short: 'أنشئ تقريرك الخاص من مجموعة بيانات ومؤشرات وأبعاد؛ مع جدول محوري ورسم بياني وحفظ ومشاركة.',
  },
  'report_builder.metrics': {
    title: 'المؤشرات والأبعاد',
    short: 'المؤشرات قيم تُجمَع؛ والأبعاد تقسّمها إلى مجموعات.',
  },
  'report_builder.pivot': {
    title: 'الجدول المحوري',
    short: 'بُعد الأعمدة يحوّل القيم إلى أعمدة (مؤشر واحد، حتى 36 عمودًا).',
  },
  'report_builder.share': {
    title: 'المشاركة',
    short: 'يرى المشاركون البيانات بصلاحياتهم الخاصة؛ ولا يعدّل التقرير إلا مالكه.',
  },
  'dashboard.widgets': {
    title: 'عناصر التقارير',
    short: 'التقارير المحفوظة في لوحة معلوماتك كجدول أو رسم بياني أو مجموع؛ شخصية، حتى 12 عنصرًا.',
  },
  'reports.print': {
    title: 'طباعة / PDF',
    short: 'يطبع التقرير عبر المتصفح؛ اختر «حفظ كملف PDF» للحصول على ملف PDF. لا تُطبع القوائم والأزرار.',
  },
  'page.report_files': {
    title: 'ملفات تقاريري',
    short: 'ملفات التقارير الكبيرة المُعدّة في الخلفية والتقارير المجدولة؛ تُحفظ الملفات لمدة 30 يومًا.',
  },
  'report_files.background': {
    title: 'إعداد في الخلفية',
    short: 'يحوّل التقرير في الخلفية إلى ملف يصل إلى 50,000 صف؛ نزّله من «ملفات تقاريري».',
  },
  'report_files.schedule': {
    title: 'تقرير مجدول',
    short:
      'يُرسَل يوميًا (اليوم السابق) أو أسبوعيًا (من الاثنين إلى الأحد السابقين) أو شهريًا (الشهر السابق)؛ ولا يُرسَل مرتين للفترة نفسها.',
  },
  'report_files.recipients': {
    title: 'المستلمون',
    short: 'أعضاء المنشأة فقط؛ ويُعدّ ملف كل مستلم بصلاحياته الخاصة. يحتوي البريد الإلكتروني على رابط فقط.',
  },
  'report_files.enabled': {
    title: 'الإرسال مفعّل',
    short: 'لا تعمل الجدولة المتوقفة؛ وعند تفعيلها يُعاد حساب موعد الإرسال التالي.',
  },
  'page.payment_disputes': {
    title: 'اعتراضات الدفع',
    short:
      'حالات رد المبلغ المدفوع (Chargeback) وطلبات المعلومات التي يبلّغ عنها البنك أو مزوّد الدفع: الحالة، والمهلة، والمستندات، والأثر المالي.',
  },
  'page.payment_dispute': {
    title: 'اعتراض الدفع',
    short: 'تفاصيل الاعتراض وحالته ومستنداته وملاحظاته وسجل تغييراته.',
  },
  'disputes.status': {
    title: 'حالة الاعتراض',
    short:
      'مفتوح ← تم تقديم المستندات ← مكسوب أو خاسر؛ و«مقبول» يعني ترك المبلغ دون منازعة. تخفّض المبالغ الخاسرة/المقبولة الربح والنقد.',
  },
  'disputes.amount': {
    title: 'مبلغ الاعتراض ورسومه',
    short: 'لا يمكن أن يتجاوز المبلغ الجزء غير المعترض عليه من الدفعة؛ وتُحتسب الرسوم أيًا كانت النتيجة.',
  },
  'disputes.documents': {
    title: 'إضافة مستند',
    short: 'أرفق المستندات الداعمة (PDF أو JPEG أو PNG؛ 10 ميغابايت كحد أقصى) كملف خاص.',
  },
  'page.price_simulation': {
    title: 'محاكاة الأسعار',
    short: 'يعرض الربح بعد الرسوم لكل منتج وللفترة إذا تغيّر السعر أو التكلفة أو الرسوم؛ دون تغيير الأسعار.',
  },
  'price_simulation.scenario': {
    title: 'السيناريو',
    short: 'غيّر السعر والتكلفة بنسبة مئوية (مثلًا 10 زيادة، -5 خصم).',
  },
  'price_simulation.fee': {
    title: 'معدّل الرسوم',
    short: 'فارغ: المعدّل الفعلي لرسوم المدفوعات المحصّلة في الفترة (وفق قواعد الرسوم لديك).',
  },
  'price_simulation.volume': {
    title: 'فترة حجم المبيعات',
    short:
      'الوحدات المباعة في متجر قائمة الأسعار خلال آخر 30 أو 90 أو 365 يومًا بما فيها اليوم (بعد خصم الإلغاءات).',
  },
  'page.marketing_summary': {
    title: 'ملخص التسويق وSEO',
    short:
      'مسار التحويل في واجهة المتجر، والعملاء الجدد وCAC، وقيمة العميل خلال 12 شهرًا، والإعلانات، والبحث العضوي، والحملات، ومصادر الطلبات.',
  },
  'marketing_summary.funnel': {
    title: 'مسار التحويل في واجهة المتجر',
    short:
      'الإضافة إلى السلة ← خطوة الطلب (بما فيها الطلبات بانتظار الدفع) ← طلب مؤكد. لا تُخزَّن المشاهدات على الخادم.',
  },
  'marketing_summary.cac': {
    title: 'تكلفة اكتساب العميل الجديد (CAC)',
    short: 'إجمالي الإنفاق الإعلاني في الفترة ÷ العملاء الجدد في الفترة.',
  },
  'marketing_summary.value': {
    title: 'قيمة العميل خلال 12 شهرًا',
    short: 'صافي مبيعات آخر 365 يومًا ÷ العملاء الذين اشتروا؛ قيمة تاريخية وليست توقعًا.',
  },
  'marketing_summary.sources': {
    title: 'مصدر آخر نقطة تواصل',
    short:
      'مصدر UTM لطلبات واجهة المتجر؛ وتُعرض الطلبات التي ليس لها موافقة على ملفات تعريف الارتباط بشكل منفصل.',
  },
  'page.multi_touch': {
    title: 'إسناد المصدر متعدد نقاط التواصل',
    short:
      'يوزّع طلبات واجهة المتجر المؤكدة على المصادر وفق نماذج أول نقطة تواصل وآخر نقطة تواصل والخطي والقائم على الموضع (U).',
  },
  'multi_touch.models': {
    title: 'نماذج إسناد المصدر',
    short:
      'أول/آخر نقطة: الطلب بالكامل لتلك النقطة. الخطي: حصص متساوية. القائم على الموضع (U): 40% لكل من الأولى والأخيرة، و20% إجمالًا للنقاط الوسطى.',
  },
  'page.workflows': {
    title: 'مسارات الأتمتة',
    short: 'المشغّل ← الشرط ← الإجراء: مسارات تتحقق من الشروط وتنفّذ الإجراءات تلقائيًا عند وقوع حدث ما.',
  },
  'page.workflow': {
    title: 'مسار الأتمتة',
    short: 'مشغّل المسار وشروطه وإجراءاته؛ والتجربة، والتشغيل/الإيقاف المؤقت، وسجل التنفيذ.',
  },
  'page.workflow_run': {
    title: 'تنفيذ الأتمتة',
    short: 'كيفية معالجة الحدث: حقول الحدث، ونتائج الشروط، وكل محاولة لكل إجراء.',
  },
  'workflows.templates': {
    title: 'أمثلة جاهزة',
    short: '10 مسارات نموذجية: 8 تُفتح هنا كمسودات؛ و2 ترسلان رسائل إلى العملاء وتعملان في أقسامهما الخاصة.',
  },
  'workflows.linked': {
    title: 'أمثلة رسائل العملاء',
    short:
      'تذكيرات السلة وإشعارات عودة المنتج إلى المخزون تصل إلى العملاء، وتُدار في أقسامها الخاصة لضمان الموافقة وİYS وحد التكرار.',
  },
  'workflows.trigger': {
    title: 'المشغّل',
    short:
      'الحدث الذي يبدأ المسار. تُلتقط الأحداث كل دقيقة؛ ولا تُعالج الأحداث التي وقعت قبل تشغيل المسار، ولا يُعالج الحدث نفسه مرتين أبدًا.',
  },
  'workflows.conditions': {
    title: 'الشروط',
    short: 'مقارنات مع حقول الحدث (10 كحد أقصى). يُسجَّل الحدث غير المطابق على أنه «تم التخطي».',
  },
  'workflows.actions': {
    title: 'الإجراءات',
    short:
      'تُنفَّذ بالترتيب (8 كحد أقصى) بصلاحيات الشخص الذي شغّل المسار. الإجراء الذي تم تخطيه لا يمنع الإجراء التالي.',
  },
  'workflows.recipients': {
    title: 'مستلمو الإشعارات',
    short:
      'أعضاء لوحة التحكم فقط (20 كحد أقصى). مع «حسب الصلاحية» تُعتمد الصلاحية وقت الإرسال، ونطاق المتجر في أحداث المتجر.',
  },
  'workflows.limits': {
    title: 'الحدود',
    short:
      'فترة الانتظار: تُتخطى الأحداث التي تقع خلال هذه المدة بعد كل تنفيذ. إذا تجاوز المسار الحد المسموح به لكل ساعة يتوقف مؤقتًا من تلقاء نفسه ويُرسَل بريد إلكتروني إلى المديرين.',
  },
  'workflows.test': {
    title: 'تجربة بالأحداث الأخيرة',
    short: 'يقيّم الشروط بآخر 5 أحداث للمشغّل دون حفظ. لا يُنفَّذ أي إجراء.',
  },
  'workflows.pause_all': {
    title: 'إيقاف الكل مؤقتًا',
    short: 'إيقاف طارئ: تتوقف جميع المسارات المشغّلة مؤقتًا، وتُلغى التنفيذات قيد الانتظار.',
  },
  'workflows.run_as': {
    title: 'الشخص الذي شغّل المسار',
    short:
      'تُنفَّذ الإجراءات بصلاحيات هذا الشخص الحالية (وقت التنفيذ)؛ ويتوقف المسار مؤقتًا من تلقاء نفسه إذا أُغلقت عضويته أو فقد صلاحية لازمة.',
  },
  'workflows.runs': {
    title: 'سجل التنفيذ',
    short:
      'كل حدث يمثل تنفيذًا: قيد الانتظار، أو قيد التشغيل، أو ناجح، أو فاشل، أو تم تخطيه، أو ملغى. تعرض التفاصيل كل محاولة لكل إجراء.',
  },
  'workflows.retry': {
    title: 'إعادة المحاولة والإلغاء',
    short:
      'تُعاد محاولة التنفيذ الفاشل بدءًا من الإجراء الذي توقف عنده (يجب أن يكون المسار مشغّلًا)؛ ويمكن إلغاء التنفيذ قيد الانتظار.',
  },
};
